import React, { useState, useEffect, useRef, useMemo } from "react";
import { Users, Search, Filter, Plus, Loader2, ChevronRight, AlertCircle, Calendar, FileText, CheckCircle2, ShieldAlert, Settings as SettingsIcon, Columns3, ChevronDown, ArrowUpDown, FolderSearch } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "../utils";
import { EmployeeProfile } from "./EmployeeProfile";
import { motion, useAnimation } from "motion/react";

type VisibleColumns = {
  department: boolean;
  role: boolean;
  contractType: boolean;
  googleDrive: boolean;
  dailyRate: boolean;
  annualRate: boolean;
  status: boolean;
};

type DriveReviewCandidate = {
  personId: string;
  personName: string;
  folderId: string;
  folderName: string;
  matchQuality: string;
  matchedCandidateName?: string;
};

const COLUMN_OPTIONS: Array<{ key: keyof VisibleColumns; label: string }> = [
  { key: "department", label: "Department" },
  { key: "role", label: "Role" },
  { key: "contractType", label: "Contract Type" },
  { key: "googleDrive", label: "Google Drive" },
  { key: "dailyRate", label: "Daily Rate" },
  { key: "annualRate", label: "Annual Rate" },
  { key: "status", label: "Status" },
];

export function People({
  onOpenSettings,
  onProfileOpenChange,
  initialSelectedPersonId,
  onInitialSelectedPersonConsumed,
}: {
  onOpenSettings?: () => void;
  onProfileOpenChange?: (isOpen: boolean, closeProfile: (() => void) | null) => void;
  initialSelectedPersonId?: string | null;
  onInitialSelectedPersonConsumed?: () => void;
} = {}) {
  const { user } = useAuth();
  const [people, setPeople] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkingDrive, setLinkingDrive] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showDriveReviewModal, setShowDriveReviewModal] = useState(false);
  const [driveReviewCandidates, setDriveReviewCandidates] = useState<DriveReviewCandidate[]>([]);
  const [processingDriveReviewId, setProcessingDriveReviewId] = useState<string | null>(null);
  const [newUserForm, setNewUserForm] = useState({
    name: "",
    email: "",
    title: "",
    department: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");
  const [view, setView] = useState<"All Staff" | "By Department" | "New Starters" | "Contract Renewals" | "Work Anniversaries" | "Birthdays" | "Missing Information" | "Missing Documents" | "Leavers">("All Staff");
  const [selectedPerson, setSelectedPerson] = useState<any | null>(null);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [sortBy, setSortBy] = useState<
    "name_asc" | "name_desc" | "department_asc" | "role_asc" | "daily_rate_desc" | "annual_rate_desc" | "status"
  >("name_asc");
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({
    department: true,
    role: true,
    contractType: true,
    googleDrive: true,
    dailyRate: true,
    annualRate: true,
    status: true,
  });
  const [peoplePreferencesReady, setPeoplePreferencesReady] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement | null>(null);
  const lastSavedPeoplePreferencesRef = useRef("");
  const settingsControls = useAnimation();

  const getPersonInitials = (name?: string) =>
    String(name || "User")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "U";

  const getPersonDisplayName = (person?: any, profile?: any) => {
    const firstName = String(profile?.firstName || "").trim();
    const lastName = String(profile?.lastName || "").trim();
    const combinedProfileName = [firstName, lastName].filter(Boolean).join(" ").trim();

    return (
      combinedProfileName ||
      String(profile?.fullName || "").trim() ||
      String(profile?.name || "").trim() ||
      String(person?.displayName || "").trim() ||
      String(person?.fullName || "").trim() ||
      String(person?.name || "").trim() ||
      "Unnamed User"
    );
  };

  useEffect(() => {
    fetchPeopleData();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const loadPeoplePreferences = async () => {
      try {
        const res = await fetch("/api/user-preferences", {
          headers: {
            "x-user-uid": user.uid,
            "x-user-email": user.email || "",
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || "Failed to load people preferences");
        }

        if (cancelled) return;
        const nextVisibleColumns = data.preferences?.people?.directory?.visibleColumns;
        if (nextVisibleColumns && typeof nextVisibleColumns === "object") {
          setVisibleColumns((current) => ({
            department: typeof nextVisibleColumns.department === "boolean" ? nextVisibleColumns.department : current.department,
            role: typeof nextVisibleColumns.role === "boolean" ? nextVisibleColumns.role : current.role,
            contractType: typeof nextVisibleColumns.contractType === "boolean" ? nextVisibleColumns.contractType : current.contractType,
            googleDrive: typeof nextVisibleColumns.googleDrive === "boolean" ? nextVisibleColumns.googleDrive : current.googleDrive,
            dailyRate: typeof nextVisibleColumns.dailyRate === "boolean" ? nextVisibleColumns.dailyRate : current.dailyRate,
            annualRate: typeof nextVisibleColumns.annualRate === "boolean" ? nextVisibleColumns.annualRate : current.annualRate,
            status: typeof nextVisibleColumns.status === "boolean" ? nextVisibleColumns.status : current.status,
          }));
        }
      } catch (error) {
        console.error("Failed to load people preferences", error);
      } finally {
        if (!cancelled) {
          setPeoplePreferencesReady(true);
        }
      }
    };

    setPeoplePreferencesReady(false);
    loadPeoplePreferences();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !peoplePreferencesReady) return;

    const nextPreferencesState = JSON.stringify(visibleColumns);
    if (lastSavedPeoplePreferencesRef.current === nextPreferencesState) return;

    lastSavedPeoplePreferencesRef.current = nextPreferencesState;
    void fetch("/api/user-preferences", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-user-uid": user.uid,
        "x-user-email": user.email || "",
      },
      body: JSON.stringify({
        preferences: {
          people: {
            directory: {
              visibleColumns,
            },
          },
        },
      }),
    }).catch((error) => {
      console.error("Failed to save people preferences", error);
    });
  }, [peoplePreferencesReady, user, visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    onProfileOpenChange?.(
      Boolean(selectedPerson),
      selectedPerson ? () => setSelectedPerson(null) : null
    );
  }, [onProfileOpenChange, selectedPerson]);

  useEffect(() => {
    if (!initialSelectedPersonId || people.length === 0) return;
    const matchedPerson = people.find((person: any) => person.id === initialSelectedPersonId);
    if (matchedPerson) {
      setSelectedPerson(matchedPerson);
      onInitialSelectedPersonConsumed?.();
    }
  }, [initialSelectedPersonId, onInitialSelectedPersonConsumed, people]);

  const fetchPeopleData = async () => {
    if (!user) return;
    try {
      const [peopleRes, profilesRes] = await Promise.all([
        fetch("/api/people", { headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" } }),
        fetch("/api/people/profiles", { headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" } })
      ]);
      
      if (peopleRes.ok) {
        const nextPeople = await peopleRes.json();
        setPeople(nextPeople);
        setSelectedPerson((current) => current ? nextPeople.find((person: any) => person.id === current.id) || current : current);
      }
      if (profilesRes.ok) setProfiles(await profilesRes.json());
    } catch (error) {
      console.error("Failed to fetch people data", error);
      toast.error("Failed to load people data");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncWorkspace = async () => {
    if (!user) return;
    setSyncing(true);
    try {
      const res = await fetch("/api/people/workspace/sync", {
        method: "POST",
        headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to sync");
      }
      toast.success("Workspace sync complete");
      fetchPeopleData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleLinkDrive = async () => {
    if (!user) return;
    setLinkingDrive(true);
    try {
      const res = await fetch("/api/people/folders/link-existing-all", {
        method: "POST",
        headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to link Google Drive folders");
      }
      const reviewNeeded = Number(data.reviewNeeded || 0);
      const reviewCandidates = Array.isArray(data.reviewCandidates) ? data.reviewCandidates : [];
      toast.success(
        `Drive linking complete. Linked ${data.linked || 0}, already linked ${data.alreadyLinked || 0}, unmatched ${data.unmatched || 0}${
          reviewNeeded > 0 ? `, review needed ${reviewNeeded}` : ""
        }.`,
      );
      if (reviewCandidates.length > 0) {
        setDriveReviewCandidates(reviewCandidates);
        setShowDriveReviewModal(true);
      }
      fetchPeopleData();
    } catch (error: any) {
      toast.error(error.message || "Failed to link Google Drive folders");
    } finally {
      setLinkingDrive(false);
    }
  };

  const handleConfirmDriveReviewCandidate = async (candidate: DriveReviewCandidate) => {
    if (!user) return;
    setProcessingDriveReviewId(candidate.personId);
    try {
      const res = await fetch(`/api/people/profiles/${candidate.personId}/folder/link-existing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: JSON.stringify({
          folderId: candidate.folderId,
          confirmLinkExisting: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to link Drive folder");
      }
      setDriveReviewCandidates((current) => current.filter((item) => item.personId !== candidate.personId));
      toast.success(`Linked ${candidate.personName} to ${candidate.folderName}`);
      await fetchPeopleData();
    } catch (error: any) {
      toast.error(error.message || "Failed to confirm Drive folder link");
    } finally {
      setProcessingDriveReviewId(null);
    }
  };

  const handleSkipDriveReviewCandidate = (candidate: DriveReviewCandidate) => {
    setDriveReviewCandidates((current) => current.filter((item) => item.personId !== candidate.personId));
  };

  useEffect(() => {
    if (driveReviewCandidates.length === 0) {
      setShowDriveReviewModal(false);
      setProcessingDriveReviewId(null);
    }
  }, [driveReviewCandidates]);

  const closeCreateUserModal = () => {
    setShowCreateUserModal(false);
    setCreatingUser(false);
    setNewUserForm({
      name: "",
      email: "",
      title: "",
      department: "",
    });
  };

  const handleCreateUser = () => {
    setShowCreateUserModal(true);
  };

  const submitCreateUser = async () => {
    if (!user) return;
    if (!newUserForm.name.trim()) {
      toast.error("Full name is required.");
      return;
    }

    setCreatingUser(true);
    try {
      const res = await fetch("/api/people", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: JSON.stringify(newUserForm),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to create user");
      }

      toast.success("User created");
      await fetchPeopleData();
      setSelectedPerson(data);
      closeCreateUserModal();
    } catch (error: any) {
      toast.error(error.message || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleSettingsMouseEnter = () => {
    settingsControls.start({ rotate: 90, scale: 1.1, transition: { duration: 0.4, ease: "easeInOut" } });
  };

  const handleSettingsMouseLeave = () => {
    settingsControls.start({ rotate: 0, scale: 1, transition: { duration: 0.3, ease: "easeInOut" } });
  };

  const departments = ["All", ...Array.from(new Set(people.map(p => p.department).filter(Boolean)))];
  const profileById = new Map(profiles.map(profile => [profile.id, profile]));

  const formatMoney = (value?: number) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Generate Alerts
  const alerts: { type: string; title: string; message: string; color: string }[] = [];
  const today = new Date();
  
  profiles.forEach(profile => {
    const person = people.find(p => p.id === profile.id);
    if (!person || !person.active) return;
    const personDisplayName = getPersonDisplayName(person, profile);

    const checkDate = (dateStr: string, days: number, title: string, messagePrefix: string, color: string) => {
      if (!dateStr) return;
      const date = new Date(dateStr);
      const diffTime = date.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 0 && diffDays <= days) {
        alerts.push({ type: title, title, message: `${personDisplayName}'s ${messagePrefix} in ${diffDays} days.`, color });
      } else if (diffDays < 0 && diffDays >= -7) {
        alerts.push({ type: title, title, message: `${personDisplayName}'s ${messagePrefix} was ${Math.abs(diffDays)} days ago.`, color: "bg-rose-500" });
      }
    };

    checkDate(profile.visaExpiryDate, 90, "Visa Expiry", "visa expires", "bg-rose-500");
    checkDate(profile.probationEndDate, 30, "Probation Review", "probation ends", "bg-amber-500");
    checkDate(profile.contractEndDate, 60, "Contract Renewal", "contract ends", "bg-rose-500");
    checkDate(profile.nextReviewDate, 30, "Performance Review", "review is", "bg-indigo-500");
    
    // Birthday (ignore year)
    if (profile.birthday) {
      const bday = new Date(profile.birthday);
      bday.setFullYear(today.getFullYear());
      if (bday < today) bday.setFullYear(today.getFullYear() + 1);
      const diffDays = Math.ceil((bday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays <= 30) {
        alerts.push({ type: "Birthday", title: "Birthday", message: `${personDisplayName}'s birthday is in ${diffDays} days.`, color: "bg-emerald-500" });
      }
    }

    // Work Anniversary
    if (profile.contractStartDate) {
      const startDate = new Date(profile.contractStartDate);
      const anniv = new Date(profile.contractStartDate);
      anniv.setFullYear(today.getFullYear());
      if (anniv < today) anniv.setFullYear(today.getFullYear() + 1);
      const diffDays = Math.ceil((anniv.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      const years = anniv.getFullYear() - startDate.getFullYear();
      if (diffDays <= 30 && years > 0) {
        alerts.push({ type: "Work Anniversary", title: "Work Anniversary", message: `${personDisplayName} (${years} years) in ${diffDays} days.`, color: "bg-indigo-500" });
      }
    }
    
    // Missing Info
    let missingInfo = [];
    if (!(profile.personalPhone || profile.phone)) missingInfo.push("phone");
    if (!(profile.address || profile.addressLine2 || profile.city || profile.stateProvince || profile.postalCode || profile.country)) {
      missingInfo.push("address");
    }
    if (!profile.emergencyContacts || profile.emergencyContacts.length === 0) missingInfo.push("emergency contact");
    if (missingInfo.length > 0) {
      alerts.push({ type: "Missing Info", title: "Missing Information", message: `${personDisplayName} is missing: ${missingInfo.join(", ")}.`, color: "bg-slate-400" });
    }
  });

  // Sort alerts by priority (closest dates first, but missing info last)
  // For simplicity, just take the first 10
  const topAlerts = alerts.slice(0, 10);

  const filteredPeople = people.filter(p => {
    const profile = profiles.find(prof => prof.id === p.id);
    const personDisplayName = getPersonDisplayName(p, profile);
    const matchesSearch = personDisplayName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = selectedDepartment === "All" || p.department === selectedDepartment;
    
    if (!matchesSearch || !matchesDept) return false;

    switch (view) {
      case "Leavers":
        return !p.active;
      case "New Starters":
        if (!p.active || !profile?.contractStartDate) return false;
        const startDate = new Date(profile.contractStartDate);
        const diffDaysStart = (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
        return diffDaysStart >= 0 && diffDaysStart <= 90; // Started in last 90 days
      case "Contract Renewals":
        return p.active && alerts.some(a => a.type === "Contract Renewal" && a.message.includes(personDisplayName));
      case "Work Anniversaries":
        return p.active && alerts.some(a => a.type === "Work Anniversary" && a.message.includes(personDisplayName));
      case "Birthdays":
        return p.active && alerts.some(a => a.type === "Birthday" && a.message.includes(personDisplayName));
      case "Missing Information":
        return p.active && alerts.some(a => a.type === "Missing Info" && a.message.includes(personDisplayName));
      case "Missing Documents":
        // TODO: Implement missing documents logic once drive integration is done
        return false;
      case "By Department":
      case "All Staff":
      default:
        return p.active;
    }
  });

  const sortPeopleList = useMemo(() => {
    const getNumericRate = (person: any, profile?: any, field: "daily" | "annual" = "daily") => {
      if (field === "daily") {
        const value = profile?.dailyRate ?? profile?.totalDailyCost ?? person.dailyRate;
        return typeof value === "number" && Number.isFinite(value) ? value : -1;
      }
      const value = profile?.salary;
      return typeof value === "number" && Number.isFinite(value) ? value : -1;
    };

    return (inputPeople: any[]) => {
      const nextPeople = [...inputPeople];

      nextPeople.sort((a, b) => {
        const profileA = profileById.get(a.id);
        const profileB = profileById.get(b.id);
        const nameA = getPersonDisplayName(a, profileA);
        const nameB = getPersonDisplayName(b, profileB);

        switch (sortBy) {
          case "name_desc":
            return nameB.localeCompare(nameA);
          case "department_asc":
            return (a.department || "ZZZ").localeCompare(b.department || "ZZZ") || nameA.localeCompare(nameB);
          case "role_asc":
            return (a.title || "ZZZ").localeCompare(b.title || "ZZZ") || nameA.localeCompare(nameB);
          case "daily_rate_desc":
            return getNumericRate(b, profileById.get(b.id), "daily") - getNumericRate(a, profileById.get(a.id), "daily") || nameA.localeCompare(nameB);
          case "annual_rate_desc":
            return getNumericRate(b, profileById.get(b.id), "annual") - getNumericRate(a, profileById.get(a.id), "annual") || nameA.localeCompare(nameB);
          case "status":
            return Number(Boolean(b.active)) - Number(Boolean(a.active)) || nameA.localeCompare(nameB);
          case "name_asc":
          default:
            return nameA.localeCompare(nameB);
        }
      });

      if (view === "By Department" && sortBy === "name_asc") {
        nextPeople.sort((a, b) => (a.department || "ZZZ").localeCompare(b.department || "ZZZ") || getPersonDisplayName(a, profileById.get(a.id)).localeCompare(getPersonDisplayName(b, profileById.get(b.id))));
      }

      return nextPeople;
    };
  }, [profileById, sortBy, view]);

  const sortedPeople = useMemo(() => sortPeopleList(filteredPeople), [filteredPeople, sortPeopleList]);

  const navigationPeople = useMemo(() => {
    if (!selectedPerson) return sortedPeople;
    if (sortedPeople.some((person) => person.id === selectedPerson.id)) {
      return sortedPeople;
    }
    return sortPeopleList([...filteredPeople, selectedPerson]);
  }, [filteredPeople, selectedPerson, sortPeopleList, sortedPeople]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (selectedPerson) {
    const profile = profiles.find(p => p.id === selectedPerson.id);
    const selectedPersonIndex = navigationPeople.findIndex((person) => person.id === selectedPerson.id);
    const previousPerson = selectedPersonIndex > 0 ? navigationPeople[selectedPersonIndex - 1] : null;
    const nextPerson =
      selectedPersonIndex >= 0 && selectedPersonIndex < navigationPeople.length - 1
        ? navigationPeople[selectedPersonIndex + 1]
        : null;
    return (
      <EmployeeProfile 
        person={selectedPerson} 
        profile={profile} 
        onBack={() => setSelectedPerson(null)} 
        onUpdate={fetchPeopleData}
        onPreviousPerson={previousPerson ? () => setSelectedPerson(previousPerson) : null}
        onNextPerson={nextPerson ? () => setSelectedPerson(nextPerson) : null}
      />
    );
  }

  return (
    <div className="flex flex-col pb-12">
      {showCreateUserModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-brandon font-medium text-slate-900 dark:text-slate-100">Create User</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Add a new internal person directly into Project Tools. Workspace, Flow Production Tracking, and other systems can link later.
                </p>
              </div>
              <button
                type="button"
                onClick={closeCreateUserModal}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-2 sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Full Name</span>
                <input
                  type="text"
                  value={newUserForm.name}
                  onChange={(event) => setNewUserForm((current) => ({ ...current, name: event.target.value }))}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-950"
                  placeholder="Emily Birkett"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Email</span>
                <input
                  type="email"
                  value={newUserForm.email}
                  onChange={(event) => setNewUserForm((current) => ({ ...current, email: event.target.value }))}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-950"
                  placeholder="emily@vinefx.com"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Department</span>
                <input
                  type="text"
                  value={newUserForm.department}
                  onChange={(event) => setNewUserForm((current) => ({ ...current, department: event.target.value }))}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-950"
                  placeholder="Comp"
                />
              </label>

              <label className="flex flex-col gap-2 sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Title</span>
                <input
                  type="text"
                  value={newUserForm.title}
                  onChange={(event) => setNewUserForm((current) => ({ ...current, title: event.target.value }))}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-950"
                  placeholder="Compositor"
                />
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeCreateUserModal}
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateUser}
                disabled={creatingUser}
                className="inline-flex items-center gap-2 rounded-md border border-[#ffcc01] bg-[#ffcc01] px-4 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {creatingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Create User
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showDriveReviewModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-brandon font-medium text-slate-900 dark:text-slate-100">Review Drive matches</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  These folders need confirmation before they are linked to people in the app.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDriveReviewModal(false)}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                Close
              </button>
            </div>

            <div className="mt-6 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              {driveReviewCandidates.map((candidate) => {
                const isProcessing = processingDriveReviewId === candidate.personId;
                return (
                  <div
                    key={`${candidate.personId}-${candidate.folderId}`}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">App person</p>
                        <p className="truncate text-base font-medium text-slate-900 dark:text-slate-100">{candidate.personName}</p>
                      </div>
                      <div className="min-w-0 flex-1 sm:max-w-md">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Proposed Drive folder</p>
                        <p className="truncate text-base font-medium text-slate-900 dark:text-slate-100">{candidate.folderName}</p>
                        {candidate.matchedCandidateName && candidate.matchedCandidateName !== candidate.folderName ? (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Matched as {candidate.matchedCandidateName}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSkipDriveReviewCandidate(candidate)}
                          disabled={isProcessing}
                          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          Skip
                        </button>
                        <button
                          type="button"
                          onClick={() => handleConfirmDriveReviewCandidate(candidate)}
                          disabled={isProcessing}
                          className="inline-flex items-center gap-2 rounded-md border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Confirm link
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <div className="bg-[#eef4ef] dark:bg-[#1d2920] px-6 py-4 rounded-lg border border-[#ccd8cc] dark:border-[#48604c] flex items-center justify-between mb-6 shadow-sm shrink-0">
        <div className="flex flex-col">
          <h2 className="text-2xl font-brandon font-medium text-slate-900 dark:text-slate-100">People & HR</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Manage staff, HR profiles, and team permissions</p>
        </div>
        <div className="flex items-center gap-3">
          {onOpenSettings ? (
            <motion.button
              onMouseEnter={handleSettingsMouseEnter}
              onMouseLeave={handleSettingsMouseLeave}
              whileTap={{ scale: 0.98 }}
              onClick={onOpenSettings}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100"
              aria-label="People & HR settings"
              title="People & HR settings"
            >
              <motion.div animate={settingsControls} initial={{ rotate: 0, scale: 1 }}>
                <SettingsIcon className="w-4 h-4" />
              </motion.div>
            </motion.button>
          ) : null}
          <button
            onClick={handleCreateUser}
            className="inline-flex items-center gap-2 rounded-md border border-[#ffcc01] bg-[#ffcc01] px-4 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:brightness-95"
          >
            <Plus className="w-4 h-4" />
            Create User
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-300 rounded-lg">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="font-medium text-slate-900 dark:text-slate-100">Total Staff</h3>
          </div>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{people.filter(p => p.active).length}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-300 rounded-lg">
              <AlertCircle className="w-5 h-5" />
            </div>
            <h3 className="font-medium text-slate-900 dark:text-slate-100">Missing Info</h3>
          </div>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{alerts.filter(a => a.type === "Missing Info").length}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300 rounded-lg">
              <Calendar className="w-5 h-5" />
            </div>
            <h3 className="font-medium text-slate-900 dark:text-slate-100">Upcoming Reviews</h3>
          </div>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{alerts.filter(a => a.type === "Performance Review").length}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-300 rounded-lg">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <h3 className="font-medium text-slate-900 dark:text-slate-100">Contract Renewals</h3>
          </div>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{alerts.filter(a => a.type === "Contract Renewal").length}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar / Views */}
        <div className="w-full md:w-64 shrink-0 flex flex-col gap-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="p-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Views</h3>
            </div>
            <div className="p-2 flex flex-col gap-1">
              {["All Staff", "By Department", "New Starters", "Contract Renewals", "Work Anniversaries", "Birthdays", "Missing Information", "Missing Documents", "Leavers"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v as any)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                    view === v ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* HR Alerts Panel */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="p-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">HR Alerts</h3>
              <span className="bg-rose-100 dark:bg-rose-950/40 text-rose-600 dark:text-rose-300 text-[10px] font-bold px-2 py-0.5 rounded-full">{alerts.length}</span>
            </div>
            <div className="p-3 flex flex-col gap-3 max-h-[400px] overflow-y-auto">
              {topAlerts.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No upcoming alerts.</p>
              ) : (
                topAlerts.map((alert, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", alert.color)} />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{alert.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{alert.message}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-2 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-xs text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400 dark:text-slate-500" />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-xs px-2 py-1 text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {departments.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <div className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-50 px-2 py-1 dark:border-slate-700 dark:bg-slate-950">
                <ArrowUpDown className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Sort</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="bg-transparent text-xs text-slate-700 dark:text-slate-100 focus:outline-none"
                >
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                  <option value="department_asc">Department</option>
                  <option value="role_asc">Role</option>
                  <option value="daily_rate_desc">Daily Rate</option>
                  <option value="annual_rate_desc">Annual Rate</option>
                  <option value="status">Status</option>
                </select>
              </div>
              <button
                onClick={handleSyncWorkspace}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#ffcc01] bg-[#ffcc01] px-3 py-1 text-xs font-medium text-slate-900 transition-colors hover:brightness-95 disabled:opacity-50"
              >
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                Sync Workspace
              </button>
              <button
                onClick={handleLinkDrive}
                disabled={linkingDrive}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                {linkingDrive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderSearch className="w-3.5 h-3.5" />}
                Link Drive
              </button>
              <div className="relative" ref={columnMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowColumnMenu((current) => !current)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Columns3 className="w-3.5 h-3.5" />
                  Columns
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {showColumnMenu ? (
                  <div className="absolute right-0 top-full z-20 mt-2 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                    {COLUMN_OPTIONS.map((column) => (
                      <label
                        key={column.key}
                        className="flex items-center gap-2 rounded-lg px-2 py-2 text-xs text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[column.key]}
                          onChange={() =>
                            setVisibleColumns((current) => ({
                              ...current,
                              [column.key]: !current[column.key],
                            }))
                          }
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
                        />
                        {column.label}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-3 font-medium">Employee</th>
                  {visibleColumns.department ? <th className="px-6 py-3 font-medium">Department</th> : null}
                  {visibleColumns.role ? <th className="px-6 py-3 font-medium">Role</th> : null}
                  {visibleColumns.contractType ? <th className="px-6 py-3 font-medium">Contract Type</th> : null}
                  {visibleColumns.googleDrive ? <th className="px-6 py-3 font-medium">Google Drive</th> : null}
                  {visibleColumns.dailyRate ? <th className="px-6 py-3 font-medium">Daily Rate</th> : null}
                  {visibleColumns.annualRate ? <th className="px-6 py-3 font-medium">Annual Rate</th> : null}
                  {visibleColumns.status ? <th className="px-6 py-3 font-medium">Status</th> : null}
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {sortedPeople.length === 0 ? (
                  <tr>
                    <td colSpan={2 + Object.values(visibleColumns).filter(Boolean).length} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
                      No staff found. Try syncing with Google Workspace.
                    </td>
                  </tr>
                ) : (
                  sortedPeople.map((person) => {
                    const profile = profileById.get(person.id);
                    const personDisplayName = getPersonDisplayName(person, profile);
                    const contractType = profile?.contractType || person.contractType;
                    const hasDriveLink = Boolean(profile?.driveFolderId || profile?.driveLinked);
                    const dailyRate = formatMoney(profile?.dailyRate ?? profile?.totalDailyCost ?? person.dailyRate);
                    const annualRate = formatMoney(profile?.salary);
                    const initials = getPersonInitials(personDisplayName);

                    return (
                      <tr 
                        key={person.id} 
                        onClick={() => setSelectedPerson(person)}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors group cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100 text-sm font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              <span>{initials}</span>
                              {person.photoUrl ? (
                                <img
                                  src={person.photoUrl}
                                  alt={personDisplayName}
                                  className="absolute inset-0 h-full w-full object-cover"
                                  referrerPolicy="no-referrer"
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : null}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900 dark:text-slate-100">{personDisplayName}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">{person.email}</div>
                            </div>
                          </div>
                        </td>
                        {visibleColumns.department ? (
                          <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                            {person.department || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
                          </td>
                        ) : null}
                        {visibleColumns.role ? (
                          <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                            {person.title || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
                          </td>
                        ) : null}
                        {visibleColumns.contractType ? (
                          <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                            {contractType || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
                          </td>
                        ) : null}
                        {visibleColumns.googleDrive ? (
                          <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                            {hasDriveLink ? (
                              <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                Linked
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                                Not linked
                              </span>
                            )}
                          </td>
                        ) : null}
                        {visibleColumns.dailyRate ? (
                          <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                            {dailyRate || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
                          </td>
                        ) : null}
                        {visibleColumns.annualRate ? (
                          <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                            {annualRate || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
                          </td>
                        ) : null}
                        {visibleColumns.status ? (
                          <td className="px-6 py-4">
                            <span className={cn(
                              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                              person.active 
                                ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300" 
                                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                            )}>
                              {person.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                        ) : null}
                        <td className="px-6 py-4 text-right">
                          <button className="text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors">
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
